import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { toast } from 'react-toastify';

import Button from '@/components/CustomButtons/Button';
import {
  cropImageToRetinaPng,
  isAcceptedLogoFile,
  isJpegFile,
  LOGO_ACCEPT_ATTRIBUTE,
} from '@/utils/logo-upload.utils';

function getInitialCrop() {
  return {
    unit: '%',
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  };
}

export default function LogoUpload({
  previewUrl,
  existingPreviewUrl,
  onChange,
  label,
  browseLabel,
  disabled = false,
  inputId = 'logo-upload',
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [sourceUrl, setSourceUrl] = useState('');
  const [crop, setCrop] = useState();
  const [processing, setProcessing] = useState(false);

  const displayedPreview = previewUrl || existingPreviewUrl;

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const closeCropDialog = useCallback(() => {
    setCropDialogOpen(false);
    setCrop(undefined);
    resetFileInput();
    setSourceUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return '';
    });
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (isJpegFile(file)) {
      toast.error(t('logoUpload.errors.jpegNotAllowed'));
      resetFileInput();
      return;
    }

    if (!isAcceptedLogoFile(file)) {
      toast.error(t('logoUpload.errors.invalidFormat'));
      resetFileInput();
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setSourceUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return objectUrl;
    });
    setCropDialogOpen(true);
  };

  const onImageLoad = () => {
    setCrop(getInitialCrop());
  };

  const handleApplyCrop = async () => {
    if (!imageRef.current || !crop?.width || !crop?.height) {
      toast.error(t('logoUpload.errors.cropRequired'));
      return;
    }

    setProcessing(true);
    try {
      const { file, displayWidth, displayHeight } = await cropImageToRetinaPng(
        imageRef.current,
        crop,
      );
      const nextPreviewUrl = URL.createObjectURL(file);

      onChange({
        file,
        previewUrl: nextPreviewUrl,
        displayWidth,
        displayHeight,
      });
      closeCropDialog();
    } catch {
      toast.error(t('logoUpload.errors.processingFailed'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <div className='custom-upload m-b-30 m-t-27'>
        <label htmlFor={inputId}>{label}</label>
        <br />
        <label htmlFor={inputId} className='custom-upload-btn'>
          <span>{browseLabel || t('common.buttons.browse')}</span>
          <input
            ref={fileInputRef}
            id={inputId}
            name={inputId}
            type='file'
            accept={LOGO_ACCEPT_ATTRIBUTE}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            disabled={disabled}
          />
        </label>
      </div>

      {displayedPreview ? (
        <Box
          className='preview-image'
          sx={{
            border: '1px solid #dddddd',
            mb: 3,
            width: 200,
            height: 150,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            minHeight: 0,
            minWidth: 0,
          }}>
          <Box
            component='img'
            src={displayedPreview}
            alt={t('logoUpload.previewAlt')}
            className='preview-image__img'
            sx={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              flexShrink: 1,
              minHeight: 0,
              minWidth: 0,
            }}
          />
        </Box>
      ) : null}
      <Dialog
        open={cropDialogOpen}
        onClose={closeCropDialog}
        maxWidth='md'
        fullWidth>
        <DialogTitle>{t('logoUpload.cropTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
            {sourceUrl ? (
              <ReactCrop
                crop={crop}
                onChange={(_pixelCrop, percentCrop) => setCrop(percentCrop)}>
                <img
                  ref={imageRef}
                  src={sourceUrl}
                  alt={t('logoUpload.cropImageAlt')}
                  onLoad={onImageLoad}
                  style={{ maxHeight: '60vh', maxWidth: '100%' }}
                />
              </ReactCrop>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            color='primary'
            onClick={closeCropDialog}
            disabled={processing}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            color='primary'
            onClick={handleApplyCrop}
            disabled={processing}>
            {processing
              ? t('logoUpload.processing')
              : t('logoUpload.applyCrop')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
